import { apiFetch } from './client'
import type { SessionUser } from './auth'

/**
 * B1 (sessao 35) - quem esta logado e qual e a marca da empresa dele.
 *
 * `GET /api/auth/me` nunca devolve 401: anonimo vem com `authenticated=false`
 * (contrato em `core_platform/identity_access/application/dtos.py`,
 * `IdentityOutput`). As permissoes servem SO para esconder item de menu; quem
 * autoriza e o backend em cada rota (ADR-0009).
 *
 * Marca do cliente (S31-10): logo SO PNG/WebP servido por https (SVG de terceiro
 * pode carregar script) e cor SO `#RRGGBB`, usada apenas na faixa fina do menu
 * -- nunca em botao ou texto. Valor fora do formato vira `null` e a tela mostra
 * o nome da empresa / nenhuma faixa. A empresa e sempre a da SESSAO (A132-b).
 */

export interface Me {
  authenticated: boolean
  user: SessionUser | null
  tenantId: string | null
  permissions: string[]
}

interface MeResponse {
  authenticated: boolean
  user: { id: string; tenant_id: string; username: string; email: string } | null
  tenant_id: string | null
  permissions?: string[]
}

export async function fetchMe(): Promise<Me> {
  const r = await apiFetch<MeResponse>('/api/auth/me')
  const u = r.user
  return {
    authenticated: r.authenticated === true && u !== null,
    user: u ? { id: u.id, tenantId: u.tenant_id, username: u.username, email: u.email } : null,
    tenantId: r.tenant_id,
    permissions: Array.isArray(r.permissions) ? r.permissions : [],
  }
}

export interface TenantBrand {
  displayName: string
  logoUrl: string | null
  stripeColor: string | null
}

interface TenantResponse {
  display_name: string
  branding: { primary_color: string | null; logo_url: string | null } | null
}

const LOGO_OK = /^https:\/\/[^\s"'<>]+\.(png|webp)(\?[^\s"'<>]*)?$/i
const HEX_OK = /^#[0-9A-Fa-f]{6}$/

/** S31-10: so https e so .png/.webp. Qualquer outra coisa -> null. */
export function safeLogoUrl(raw: unknown): string | null {
  return typeof raw === 'string' && LOGO_OK.test(raw.trim()) ? raw.trim() : null
}

/** S31-10: so `#RRGGBB`. Nome de cor, rgb(), url() ou lixo -> null. */
export function safeHexColor(raw: unknown): string | null {
  return typeof raw === 'string' && HEX_OK.test(raw.trim()) ? raw.trim() : null
}

export async function fetchTenantBrand(tenantId: string): Promise<TenantBrand> {
  const r = await apiFetch<TenantResponse>(`/api/tenants/${encodeURIComponent(tenantId)}`)
  return {
    displayName: r.display_name,
    logoUrl: safeLogoUrl(r.branding?.logo_url),
    stripeColor: safeHexColor(r.branding?.primary_color),
  }
}

/** Itens do menu e a permissao que cada um exige (seed RBAC, medido em 28/09). */
export const MENU_PERMISSIONS = {
  units: 'organization_core.unit.read',
  jobs: 'job_architecture.job.read',
  aiDescription: 'ai_services.use',
  // s65 / PE: o perfil da empresa (ler o proprio tenant)
  company: 'tenant.read',
  // s69 / 2x: Usuarios (seed iam_admin_rbac_seed_v1: tenant_admin e platform_admin)
  users: 'iam.user.list',
} as const

export const ME_QUERY_KEY = ['me'] as const

/** s54: empresas que o administrador da Gente pode escolher (GET /api/tenants exige tenant.list). */
export interface TenantOption {
  id: string
  displayName: string
}

interface TenantListItem {
  id: string
  code: string
  display_name: string | null
}

export async function fetchTenantOptions(): Promise<TenantOption[]> {
  const r = await apiFetch<TenantListItem[]>('/api/tenants?limit=200&offset=0')
  return (Array.isArray(r) ? r : [])
    .map((x) => ({ id: x.id, displayName: x.display_name || x.code }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'pt-BR'))
}

/**
 * s56c (pedido de Marcelo): o nome na TELA com a primeira letra maiuscula. So exibicao -- o login nao muda.
 * Login em forma de e-mail vira nome ("ana.souza@lutepel.com.br" -> "Ana Souza"); ponto, hifen e sublinhado separam palavras;
 * particulas (da, de, do, das, dos, e) no meio ficam em minuscula.
 */
const PARTICULAS = new Set(['da', 'de', 'do', 'das', 'dos', 'e'])

export function nomeParaTela(login: string): string {
  const base = (login.split('@')[0] ?? '').trim()
  return base
    .split(/[.\s_-]+/)
    .filter((p) => p !== '')
    .map((p, i) => {
      const l = p.toLowerCase()
      return i > 0 && PARTICULAS.has(l) ? l : l.charAt(0).toUpperCase() + l.slice(1)
    })
    .join(' ')
}
